// @ts-strict-ignore
import invariant from 'invariant';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import styled from 'styled-components';

import { DonateBlockData } from 'api/cms';
import { makeDonation } from 'api/donations';
import { PublicPage } from 'api/website';
import { Box } from 'components/Box';
import SocialButtons from 'components/CmsSocialButtons';
import Divider from 'components/Divider';
import { Link } from 'components/Link';
import { Body, RichText } from 'components/Text';
import { useCurrentUser } from 'store/main/hooks';
import {
  breakpoints,
  colors,
  fontWeights,
  gutterWidth,
  maxWidth,
  textStyles,
} from 'theme';
import { trackEvent } from 'utils/analytics';
import { getCookieValue } from 'utils/cookies';
import { isLoggedIn } from 'utils/currentUser';
import { filterObject, mapObject } from 'utils/functional';
import { getImageSrc } from 'utils/getImageSrc';
import scrollIntoView from 'utils/scrollIntoView';
import { snakeCaseToCamelCase } from 'utils/string';
import { hiDPI } from 'utils/style';

import { DonateBlockInternalForm } from './DonateBlockInternalForm';

// TODO: Fix this the next time the file is edited.
// eslint-disable-next-line react/jsx-props-no-spreading
const Container = styled(({ imageHandle, ...props }) => <Box {...props} />)`
  background-attachment: fixed;
  background-size: cover;
  background-position: center;
  ${({ imageHandle, mimetype }) =>
    imageHandle &&
    `
    background-image: url('${getImageSrc({
      handle: imageHandle,
      mimetype,
      width: 800,
    })}');
    ${hiDPI()}: {
      background-image: url('${getImageSrc({
        handle: imageHandle,
        mimetype,
        width: 1600,
      })}');
    }
    @media (min-width: ${breakpoints[3]}) {
      background-image: url('${getImageSrc({
        handle: imageHandle,
        mimetype,
        width: 1280,
      })}');
      ${hiDPI()}: {
        background-image: url('${getImageSrc({
          handle: imageHandle,
          mimetype,
          width: 2560,
        })}');
      }
    }
  `}
`;
const Headline = styled(
  ({
    text,
    isPageHeader,
    ...props
  }: {
    text: string;
    isPageHeader: boolean;
  }) => (
    <RichText
      dangerouslySetInnerHTML={{
        __html: `<${isPageHeader ? 'h1' : 'h2'}>${
          text ? text.replace(/<(\/?)(p|h1|h2)>/g, '<$1span>') : ''
        }</${isPageHeader ? 'h1' : 'h2'}>`,
      }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  )
)`
  && {
    h1,
    h2 {
      ${textStyles.h2};
    }
  }
`;
const StyledSocialButtons = styled((props) => (
  <Box
    className={props.className}
    display="flex"
    my={20}
    mx="-6px"
    flexDirection="row"
    data-qa-id="donate-thank-you-social-buttons"
  >
    {/* TODO: Fix this the next time the file is edited. */}
    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
    <SocialButtons {...props} />
  </Box>
))`
  a {
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    color: white;
    display: flex;
    flex: 1 1 auto;
    margin: 0 6px;
    padding: 3px;
    &:nth-of-type(1),
    &:nth-of-type(1):hover {
      background: #3b5998;
    }
    &:nth-of-type(2),
    &:nth-of-type(2):hover {
      background: #55acee;
    }
    &:nth-of-type(3),
    &:nth-of-type(3):hover {
      background: #777;
    }
    div {
      margin: 0 auto;
    }
  }
`;

type Props = {
  data: DonateBlockData;
  isPageHeader?: boolean;
  content: PublicPage;
};

export function DonateBlock({
  data: { headline, body, thankYouHeadline, thankYouBody, campaignId, bgImage },
  isPageHeader,
  content,
}: Props) {
  const currentUser = useCurrentUser();
  const location = useLocation();
  useEffect(() => {
    trackEvent('View Donate Form', {
      amount: 0,
      campaign_id: campaignId,
    });
  }, [campaignId]);
  const [invoiceId, setInvoiceId] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submittedAmount, setSubmittedAmount] = useState(25);

  const onSubmit = ({ handleError, updateSubmitting, values }) => {
    const extraParamsFromCookie = JSON.parse(getCookieValue('extraParams'));
    const extraParamsFromSearch = filterObject(
      queryString.parse(location.search),
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'startsWith' does not exist on type 'stri... Remove this comment to see the full error message
      (k) => k.startsWith('utm') || k === 'sf_campaign_id'
    );
    const extraParams = mapObject(
      { ...extraParamsFromCookie, ...extraParamsFromSearch },
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
      (k, v) => [snakeCaseToCamelCase(k), v]
    );
    const amount =
      values.amount === 'OTHER'
        ? values.otherAmount
        : values.amount && values.amount.toString();
    makeDonation({
      ...values,
      amount,
      campaignId: campaignId ? campaignId.toString() : null,
      ...extraParams,
    })
      .then((result) => {
        if (result.success) {
          trackEvent('Success: Created Donation', {
            amount:
              values.amount === 'OTHER' ? values.otherAmount : values.amount,
            campaign_id: campaignId,
          });
          invariant(result.invoiceIds, 'Missing invoiceIds');
          invariant(result.invoiceIds.length > 0, 'Missing invoiceIds');
          setInvoiceId(result.invoiceIds[0]);
          setSubmitted(true);
          setSubmittedAmount(amount);
        } else {
          invariant(result.error, 'missing error');
          // Convert Stripe error to general form error shape
          const error = {
            responseJson: {
              errors: [
                {
                  name: 'general',
                  type: result.error.stripeCode,
                  description: result.errorHuman,
                },
              ],
            },
          };
          trackEvent('Error: Processing Payment', {
            amount: values.amount,
            campaign_id: campaignId,
            error_type: result.error.stripeCode,
          });
          handleError(error);
        }

        scrollIntoView();
        updateSubmitting(false);
      })
      .catch((e) => {
        const { errors: errorArray } = e.responseJson;
        scrollIntoView();

        if (errorArray) {
          const errors = errorArray.reduce(
            (acc, { name, code, description, data }) => {
              if (name === 'general') return acc;
              // eslint-disable-next-line no-param-reassign
              acc[name] = {
                name,
                code,
                description,
                data: data || {},
              };
              return acc;
            },
            {}
          );
          let requiredErrorMatched = false;
          Object.keys(errors).map((field) => {
            if (errors[field] && errors[field].code) {
              trackEvent('Error: Submitting Donation', {
                amount: values.amount,
                error_type: errors[field].code,
                campaign_id: campaignId,
              });
            } else if (
              errors[field].description === 'Required' &&
              !requiredErrorMatched
            ) {
              trackEvent('Error: Submitting Donation', {
                amount: values.amount,
                error_type: 'required_fields_missing',
                campaign_id: campaignId,
              });
              requiredErrorMatched = true;
            }

            return true;
          });
        }

        handleError(e);
        updateSubmitting(false);
      });
  };

  const initialValues = isLoggedIn(currentUser)
    ? {
        amount: '',
        email: currentUser.email,
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        country: currentUser.location ? currentUser.location.countryCode : null,
      }
    : {
        country: 'US',
      };
  return (
    <Container data-qa-id="donate">
      <Container
        imageHandle={bgImage ? bgImage.handle : null}
        mimetype={bgImage ? bgImage.mimetype : null}
        minHeight={['100%', null, null, 'calc(100vh - 123px)']}
      >
        <Box
          mx="auto"
          p={[0, null, gutterWidth / 2, gutterWidth]}
          maxWidth={maxWidth[3]}
          width={1}
        >
          <Box>
            <Box
              width={['100%', null, null, `calc(50% + ${gutterWidth * 2}px)`]}
              p={[gutterWidth / 2, null, null, gutterWidth]}
              borderRadius={[0, 0, 12]}
              bg="white"
              boxShadow="0 0 6px rgba(0,0,0,0.3)"
            >
              {submitted ? (
                <Box
                  data-qa-id="donate-thank-you"
                  display={['block', null, 'flex']}
                  width="100%"
                >
                  <Box
                    display="flex"
                    flex="2"
                    flexDirection="column"
                    pr={gutterWidth / 2}
                  >
                    <Box
                      bg={colors.lightGrey}
                      color={colors.waterBlue}
                      borderRadius={15}
                      fontWeight={fontWeights.semiBold}
                      fontSize={[
                        // TODO: Fix this the next time the file is edited.
                        // eslint-disable-next-line no-nested-ternary
                        submittedAmount > 9999
                          ? 48
                          : submittedAmount > 999
                          ? 64
                          : 80,
                      ]}
                      py={40}
                      px={20}
                      classes={['flex', 'justify-center', 'align-center']}
                      data-qa-id="donate-thank-you-submitted-amount"
                      small={submittedAmount > 999}
                    >
                      {`$${submittedAmount} USD`}
                    </Box>
                    <StyledSocialButtons
                      content={content}
                      message={content.metaDescription}
                      title={content.title}
                      subsite={content.subsite.publicName}
                    />
                    <Link
                      data-qa-id="donate-thank-you-receipt-link"
                      href={`/donation-receipts/${invoiceId}`}
                      target="donate_thank_you_receipt"
                    >
                      <Body textAlign="center">{getText('View Receipt')}</Body>
                    </Link>
                  </Box>
                  <Box
                    flex="3"
                    pl={[0, null, gutterWidth / 2]}
                    mt={[gutterWidth, null, 0]}
                  >
                    <Headline
                      text={thankYouHeadline}
                      isPageHeader={isPageHeader}
                    />
                    <Body
                      dangerouslySetInnerHTML={{
                        __html: thankYouBody,
                      }}
                      mt={12}
                    />
                  </Box>
                </Box>
              ) : (
                <>
                  <Headline text={headline} isPageHeader={isPageHeader} />
                  <Body
                    dangerouslySetInnerHTML={{
                      __html: body,
                    }}
                    mt={12}
                  />

                  <Divider
                    light
                    my={[gutterWidth / 2, null, null, gutterWidth]}
                  />

                  <DonateBlockInternalForm
                    onSubmit={onSubmit}
                    campaignId={campaignId}
                    initialValues={initialValues}
                    loggedIn={isLoggedIn(currentUser)}
                  />
                </>
              )}
            </Box>
          </Box>
        </Box>
      </Container>
      )
    </Container>
  );
}
